import { Component } from '@angular/core';
import { TrendReportService } from '../services/trend-report.service'
import { ActivatedRoute } from '@angular/router';
import { LocationInformationService } from '../services/location-information.service';
import { LineSelected } from '../models/genericLamba';
import { FormGroup, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { TrendService } from '../services/trend.service';
import { LoadingService } from '../services/loading.service';
import { MatFormField, MatSelect, MatOption } from '@angular/material/select';
import { NgFor, NgIf } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { DateSelectionComponent } from '../date-selection/date-selection.component';

import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent, CanvasRenderer]);

dayjs.extend(utc);
dayjs.extend(timezone);

interface LineForm {
  lineSelected: FormControl<string>;
}


@Component({
    selector: 'app-trend-chart',
    templateUrl: './trend-chart.component.html',
    styleUrls: ['./trend-chart.component.scss'],
    imports: [ReactiveFormsModule, MatFormField, MatSelect, NgFor, MatOption, MatButton, DateSelectionComponent, NgIf, NgxEchartsDirective],
    providers: [provideEchartsCore({ echarts })]
})
export class TrendChartComponent  {
  chartOption: any;
  dataSource: any;
  trendChartData: any;
  trendChartDataArrAllShifts: any[] = [];
  trendChartDataArr1stShift: any[] = [];
  trendChartDataArr2ndShift: any[] = [];
  trendChartDataArr3rdShift: any[] = [];
  shiftDateLabels: any[] = [];
  plant: string;

  public fromDateString: string;
  public toDateString: string;

  fg_hdr: FormGroup;
  manualInput = false;
  showChart = false;
  public lineForm: FormGroup<LineForm>;
  public line: LineSelected;
  public YAxisVal = 0;
  currentShift = 'True';
  lines: { Line: string }[] = [];

  constructor(
    private locationInformationService: LocationInformationService,
    private trendReportService: TrendReportService,
    private route: ActivatedRoute,
    private trendService: TrendService,
    private loadingService: LoadingService,
    private formBuilder: FormBuilder) {
    this.plant = this.route.snapshot.paramMap.get('plant')!;
    const lineFromRoute = this.route.snapshot.paramMap.get('line');

    this.line = {
      lineSelected: lineFromRoute ?? ''
    };

    this.locationInformationService.changeLocation(true);
    this.locationInformationService.changePlant(this.plant);
    this.locationInformationService.changePlantView(true);
    this.fg_hdr = this.formBuilder.group({});
  }

  ngOnInit() {
    this.fromDateString =dayjs().subtract(7,'d').format('YYYY-MM-DD');
    this.toDateString = dayjs().format('YYYY-MM-DD');
    this.lineForm = this.formBuilder.group({
      lineSelected: [this.line.lineSelected]
    });

    this.lineForm.valueChanges.subscribe(val => {
      this.line = { lineSelected: val.lineSelected ?? '' };
    });
    this.loadingService.show()   //  start loader
    setTimeout(() => {
      if (this.fromDateString && this.toDateString) {
        this.fetch();
      }
    });

    this.trendService.getLines(this.plant).subscribe(res => {
      this.lines = res.Body;
    });
  }

  changeLineSelection() {
    this.fetch();
  }

  fetch() {
    this.trendChartDataArrAllShifts = [];
    this.trendChartDataArr1stShift = [];
    this.trendChartDataArr2ndShift = [];
    this.trendChartDataArr3rdShift = [];

    const groupBy = 'day';
    const lineSelect = this.line?.lineSelected?.length ? this.line.lineSelected : null;

    this.loadingService.show()

    this.trendReportService
      .trendDateRange(this.plant, this.fromDateString, this.toDateString, groupBy, lineSelect)
      .subscribe({
        next: response => {
          this.trendChartData = response.Body;
          this.shiftDateLabels = [];

          if (this.trendChartData.length > 0) {
            const uniqueDates = this.trendChartData
              .map(obj => obj.ShiftDate.substring(5, 10))
              .filter((elem, index, self) => index === self.indexOf(elem));

            this.shiftDateLabels = uniqueDates;

            this.trendChartDataArrAllShifts = this.trendChartData.reduce((acc, obj) => {
              if (obj.Shift !== '') {
                const existing = acc.find(item => item.label === obj.ShiftDate.substring(5, 10));
                if (existing) {
                  existing.value += obj.Score;
                } else {
                  acc.push({ label: obj.ShiftDate.substring(5, 10), value: obj.Score });
                }
              }
              return acc;
            }, []);

          this.trendChartDataArr1stShift = this.trendChartData
              .filter(obj => obj.Shift === '1ST SHIFT')
              .map(obj => ({ label: obj.ShiftDate.substring(5, 10), value: obj.Score }));

            this.trendChartDataArr2ndShift = this.trendChartData
              .filter(obj => obj.Shift === '2ND SHIFT')
              .map(obj => ({ label: obj.ShiftDate.substring(5, 10), value: obj.Score }));

            this.trendChartDataArr3rdShift = this.trendChartData
              .filter(obj => obj.Shift === '3RD SHIFT')
              .map(obj => ({ label: obj.ShiftDate.substring(5, 10), value: obj.Score }));

            this.YAxisVal =
              this.trendChartDataArrAllShifts.length > 0 ? Math.max(...this.trendChartDataArrAllShifts.map(o => o.value)) + 1 : 1;

            if (!this.YAxisVal || this.YAxisVal <= 0 || this.YAxisVal === Infinity) {
              this.YAxisVal = 1;
            }

            this.buildChartOption();
            this.showChart = true;
          } else {
            this.showChart = false;
          }                         
           },   
        error: () => {
          this.showChart = false;
        },
        complete: () => {
          this.loadingService.hide();
        }
      });
  }

  seriesNames = ['All Shifts', '1st Shift', '2nd Shift', '3rd Shift'];

  seriesColors: Record<string, string> = {
    'All Shifts': '#5b6ee1',
    '1st Shift': '#7ed957',
    '2nd Shift': '#49557a',
    '3rd Shift': '#f5a623'
  };

  formatYAxisLabel(value: number): string {
    if (value === 0) return '0';
    if (value >= 1000) {
      const k = value / 1000;
      return Number.isInteger(k) ? `${k}K` : `${k.toFixed(2)}K`;
    }
    return `${value}`;
  }

  createSeries(name: string, dataArr: any[]) {
    const color = this.seriesColors[name];
    const dataMap = new Map(dataArr.map(x => [x.label, x.value]));

    return {
      name,
      type: 'line',
      smooth: false,
      symbol: 'circle',
      symbolSize: 6,
      showSymbol: true,
      lineStyle: {
        width: 1,
        color
      },
      itemStyle: {
        color: '#ffffff',
        borderColor: color,
        borderWidth: 1
      },
      data: this.shiftDateLabels.map(d => dataMap.get(d) ?? null)
    };
  }

  getTooltipFormatter(params: any[]): string {
    if (!params || !params.length) {
      return '';
    }

    const dateLabel = params[0].axisValueLabel || params[0].axisValue;

    const validParams = params.filter((p: any) =>
      p.data !== null &&
      p.data !== undefined &&
      p.data !== '' &&
      p.data !== '-'
    );

    if (!validParams.length) {
      return `<div>${dateLabel}</div>`;
    }

    const rows = validParams.map((p: any) => {
      const color = this.seriesColors[p.seriesName] || '#999';
      const value = Number(p.data).toLocaleString();

      const marker = `
        <span style="display:inline-block; width:10px;height:10px; margin-right:6px; border:2px solid ${color}; border-radius:50%; background:#fff; vertical-align:middle;
          box-sizing:border-box;"></span>`;

      return `
        <div style="display:flex; justify-content:space-between; gap:16px; align-items:center;">
          <span>${marker}${p.seriesName}</span>
          <span><b>${value}</b></span>
        </div>
      `;
    }).join('');

    return `
      <div style="min-width:170px;">
        <div style="margin-bottom:6px;">${dateLabel}</div>
        ${rows}
      </div>
    `;
  }

  buildChartOption() {
    const shouldRotateDates = this.shiftDateLabels.length >= 30;

    this.chartOption = {
      title: {
        text: 'EOLA Score By Plant',
        left: 'center',
        top: 10,
        textStyle: {fontSize: 14, fontWeight: '600', color: '#666'}
      },

      tooltip: {
        trigger: 'axis',
        backgroundColor: '#fff',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {color: '#444'},
        formatter: (params: any[]) => this.getTooltipFormatter(params)
      },

      legend: {
        orient: 'vertical',
        right: 10,
        top: 'middle',
        itemWidth: 14,
        itemHeight: 14,
        textStyle: {color: '#666', fontSize: 12},
        data: this.seriesNames
      },

      grid: { left: 85, right: 100, top: 60, bottom: 70, containLabel: false },

      xAxis: {
        type: 'category',
        name: 'Date',
        nameLocation: 'middle',
        nameGap: 35,
        data: this.shiftDateLabels,
        boundaryGap: false,
        nameTextStyle: { color: '#666', fontSize: 14 },
        axisLine: { lineStyle: { color: '#ccc' } },
        axisTick: { show: false },
        axisLabel: { rotate: shouldRotateDates ? 45 : 0, interval: 0, hideOverlap: true, color: '#666', fontSize: 11 },
        splitLine: { show: false }
      },

      yAxis: {
        type: 'value',
        name: 'EOLA Score',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 55,
        min: 0,
        max: this.YAxisVal,
        axisLine: {show: false},
        axisTick: { show: false },
        axisLabel: { color: '#666', fontSize: 12,
          formatter: (value: number) => this.formatYAxisLabel(value)
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#e9edf5', type: 'solid'}
        }
      },

      series: [
        this.createSeries('All Shifts', this.trendChartDataArrAllShifts),
        this.createSeries('1st Shift', this.trendChartDataArr1stShift),
        this.createSeries('2nd Shift', this.trendChartDataArr2ndShift),
        this.createSeries('3rd Shift', this.trendChartDataArr3rdShift)
      ]
    };
  }

  onDateChange(e: any) {
    this.fromDateString = e.fromDate;
    this.toDateString = e.toDate;
    this.currentShift = e.currentShift;

    this.fetch();
  }
}